import { debounce } from 'lodash';
import { useCallback, useMemo, useReducer } from 'react';

import {
  ContentCategory,
  contentHierarchy,
  fall24ContentHierarchy,
} from '../utils/authenticatedHomeUtils';
import useMixpanelFlag from './useMixpanelFlag';

type SearchReducerstate = {
  searchPhrase?: string;
  subjectId?: string;
  grades?: string[];
  categoryId?: string;
};
type SearchReducerAction = {
  type: 'alterFilter' | 'clearFilters' | 'changeSearchPhrase';
  payload?: {
    categoryId?: string;
    subjectId?: string;
    grades?: string[];
    searchPhrase?: string;
  };
};

const useGeneratorsDesignerSearchFilter = () => {
  const [searchState, dispatchSearchState] = useReducer(searchReducer, {});
  const isFall24ContentHierarchyEnabled = useMixpanelFlag('FALL_24_CONTENT_HIERARCHY');

  const handleCategoryChanged = ({ categoryId }: { categoryId?: string }) => {
    dispatchSearchState({
      type: 'alterFilter',
      payload: { categoryId },
    });
  };

  const handleClearFilters = () => {
    dispatchSearchState({
      type: 'clearFilters',
    });
  };

  const handleFiltersChanged = ({
    grades,
    subjectId,
  }: {
    subjectId?: string;
    grades?: string[];
  }) => {
    dispatchSearchState({
      type: 'alterFilter',
      payload: { subjectId, grades },
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearchValueChange = useCallback(
    debounce((newSearchValue?: string) => {
      dispatchSearchState({
        type: 'changeSearchPhrase',
        payload: { searchPhrase: newSearchValue },
      });
    }, 200), // the small debounce here is to optimize rendering performance
    [],
  );

  const activeContentMap = useMemo(() => {
    const hierarchy = isFall24ContentHierarchyEnabled
      ? fall24ContentHierarchy
      : contentHierarchy;
    const activeContentMap = hierarchy.find(subjectData => {
      // When no subject is selected we default to "none" in the content hierarchy
      if (!searchState.subjectId) return subjectData.subject === 'none';
      if (subjectData.subject === searchState.subjectId) {
        return subjectData;
      }
    });
    if (activeContentMap) {
      return activeContentMap;
    }
    return hierarchy[0];
  }, [searchState.subjectId, isFall24ContentHierarchyEnabled]);

  const activeCategory = useMemo(() => {
    const currentActive = activeContentMap?.categories.find(category => {
      if (!searchState.categoryId) return category.id === 'for_you';
      return searchState.categoryId === category.id;
    });
    if (currentActive) {
      return currentActive;
    }

    return activeContentMap.categories[0] as ContentCategory | undefined;
  }, [activeContentMap, searchState.categoryId]);

  return {
    grades: searchState.grades,
    subjectId: searchState.subjectId,
    searchPhrase: searchState.searchPhrase,

    activeCategory,
    activeContentMap,
    visibleCategories: activeContentMap?.categories || [],

    handleCategoryChanged,
    handleGeneratorSearchChange: debounceSearchValueChange,
    handleFiltersChanged,
    handleClearFilters,
  };
};

const searchReducer = (
  state: SearchReducerstate,
  action: SearchReducerAction,
): SearchReducerstate => {
  switch (action.type) {
    case 'alterFilter': {
      const isSubjectChanged = !!action.payload?.subjectId;

      return {
        ...state,
        // Reset category if subject is changed
        categoryId: isSubjectChanged
          ? undefined
          : action.payload?.categoryId || state.categoryId,
        grades: action.payload?.grades || state.grades,
        subjectId: action.payload?.subjectId || state.subjectId,
      };
    }
    case 'clearFilters':
      return {
        ...state,
        categoryId: undefined,
        grades: undefined,
        subjectId: undefined,
      };
    case 'changeSearchPhrase':
      return {
        ...state,
        searchPhrase: action.payload?.searchPhrase,
        // Currently we only search for title, so we reset all filters when the search changes
        categoryId: undefined,
        grades: undefined,
        subjectId: undefined,
      };
    default:
      return state;
  }
};

export default useGeneratorsDesignerSearchFilter;
